import { useEffect } from 'react';

import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback, { LoadFn, UseApiCallbackOptions } from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useDebounced from 'lib/hooks/useDebounced';

interface UseApiLoaderArgs {
  /**
   * Unlike in `useApiCallback`, `loader` cannot be called with arguments.
   * If you want to pass arguments, wrap it in a nullary arrow function.
   *
   * INCORRECT:
   * ```typescript
   * const loadInquiry => (id: string) => API.getInquiry(id)
   * useApiCallback(loadInquiry)
   * // loadInquiry will not be called with any argument
   * ```
   *
   * CORRECT:
   * ```typescript
   * const loadInquiry => (id: string) => API.getInquiry(id)
   * useApiCallback(() => loadInquiry(theIdYouWantToLoad))
   * ```
   */
  loader: LoadFn;
  deps: unknown[];
  onError: (error: ApiResponseError) => void;
  onNetworkError: (error: ApiNetworkError) => void;
  skip?: boolean;
  abortController?: AbortController;
  options?: UseApiCallbackOptions;
}

/**
 * @name `useApiLoader`
 *
 * A hook that combines `useApiCallback` and `useApiErrorHandler`. Takes a
 * thunk that returns a promise, and wraps it with loading and error handling.
 *
 * Can be thought of as `useEffect` without a cleanup callback that also
 * provides handlers for loading state and errors.
 */
export default ({
  loader,
  deps,
  onError,
  onNetworkError,
  skip,
  abortController,
  options = {},
}: UseApiLoaderArgs) => {
  const { error, loading, callback } = useApiCallback(loader, deps, {
    defaultLoading: !skip,
    ...options,
  });
  useApiErrorHandler({ error, onError, onNetworkError });

  const [debouncedCallback] = useDebounced(callback, 500, {
    leading: true,
    trailing: true,
  });
  /**
   * Note that `useApiLoader` will call your loader function with no arguments.
   */
  useEffect(() => {
    !skip && debouncedCallback();

    if (abortController) {
      return () => abortController.abort();
    }
  }, deps);

  return { error, loading, callback };
};
